import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      // The session lives only in the httpOnly cookie; drop any token older versions stored.
      localStorage.removeItem('token');
      const safeFetch = window.__nativeFetch || window.fetch;

      const response = await safeFetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
        setLoading(false);
        return false;
      }
      
      const userData = await response.json();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
      return false;
    }
  }, []);

  const login = async (email, password) => {
    // Use native fetch saved before analytics scripts wrap it — avoids DataCloneError
    const safeFetch = window.__nativeFetch || window.fetch;
    const response = await safeFetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    setUser(data.user);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  };

  const loginWithClerkToken = async (clerkToken) => {
    const safeFetch = window.__nativeFetch || window.fetch;
    const response = await safeFetch(`${API_URL}/api/auth/clerk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token: clerkToken })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Google sign-in failed');
    }
    setUser(data.user);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setSelectedCampaign(null);
    setSelectedBusiness(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      loginWithClerkToken,
      logout,
      checkAuth,
      selectedCampaign,
      setSelectedCampaign,
      selectedBusiness,
      setSelectedBusiness
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
